import React from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import { isAdfDocEmpty } from './admin-controls/utils/isAdfDocEmpty.ts';
import { parseAdfString } from './admin-controls/utils/parseAdfString.ts';

interface SpaceAccessDisabledMessageProps {
	messageContent: string | null | undefined;
	fallback: React.ReactNode;
}

// AFTER_PAINT is the only valid priority: it must be <= AFTER_PAINT to render during SSR, and must
// not outrank its loadable ancestor RestrictedSpace, which is AFTER_PAINT. See go/cc-loading-phases.
const AdfMessageRendererLoader = LoadableAfterPaint<{ adfDoc: DocNode }>({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-AdfMessageRenderer" */ './AdfMessageRenderer.tsx'))
			.AdfMessageRenderer,
});

/**
 * Renders the admin-configured ADF message for the space access requests disabled screen.
 * Falls back to the provided `fallback` node if `messageContent` is null/empty/invalid, or if
 * the stored document has no content (the editor saves an empty paragraph rather than null).
 */
export const SpaceAccessDisabledMessage = ({
	messageContent,
	fallback,
}: SpaceAccessDisabledMessageProps): React.JSX.Element => {
	const adfDoc: DocNode | undefined = parseAdfString(messageContent);

	if (!adfDoc || isAdfDocEmpty(adfDoc)) {
		return <>{fallback}</>;
	}

	// Without a local boundary, malformed ADF would throw up to the one wrapping
	// RestrictedSpaceScreen and take the whole screen down, including the "Go home" button.
	return (
		<GenericErrorBoundary
			attribution={Attribution.PERMISSIONS_EXPERIENCE}
			renderOnError={() => <>{fallback}</>}
		>
			<AdfMessageRendererLoader adfDoc={adfDoc} />
		</GenericErrorBoundary>
	);
};
